import _ from "lodash"
import pointInPolygon from "@turf/boolean-point-in-polygon"
import { getCenterFromGeometry } from "utils/geometry"
import findDistance from "@turf/distance"
import { polygon, point, multiPolygon } from "@turf/helpers"
import { VERTICAL_TRAVERSAL_CATEGORY } from "constant"
import { getFeatureDetail } from "utils/feature"

// Distance considered to be nearby
const NEARBY_DISTANCE = 0.03 //30 meter

const BASE_DISTANCE = 1
const ESCALATOR_DISTANCE = 1
const ELEVATOR_DISTANCE = 2
const STAIR_DISTANCE = 5

const createAdjacentUnitDistance = (
  units,
  direction,
  distance = BASE_DISTANCE
) => {
  try {
    //Create graph
    const graphObj = units.reduce((acc, origin, index) => {
      if (index === units.length - 1) return acc
      const destination = units[index + 1]
      const relationObj = {}
      relationObj[origin.id] = { [destination.id]: distance }
      if (direction === "undirected")
        relationObj[destination.id] = { [origin.id]: distance }
      // return acc
      return _.merge(acc, relationObj)
    }, {})
    return graphObj
  } catch (error) {
    console.log(error)
  }
}

const createVerticalStepDistance = (relationship, distance = BASE_DISTANCE) => {
  const { intermediary, direction } = relationship.properties
  return createAdjacentUnitDistance(intermediary, direction, distance)
}

const createElevatorDistance = (relationship) => {
  const { origin, intermediary, destination, direction } =
    relationship.properties
  const elevators = [origin, ...(intermediary || []), destination]
  return createAdjacentUnitDistance(elevators, direction, ELEVATOR_DISTANCE)
}

const createTraversalDistance = (relationship) => {
  const { origin, intermediary, destination, direction } =
    relationship.properties
  const relationObj = {}
  try {
    const originCategory = _.get(origin, "properties.category")
    const destinationCategory = _.get(destination, "properties.category")
    // Allow Relationship without opening for relationship with unenclosedarea
    if (
      !intermediary &&
      ![originCategory, destinationCategory].includes("unenclosedarea")
    )
      throw new Error("Non-unenclosedarea Relationship without intermediary")
    relationObj[origin.id] = { [destination?.id || null]: BASE_DISTANCE }
    if (direction === "undirected" && destination) {
      relationObj[destination.id] = { [origin.id]: BASE_DISTANCE }
    }
    return relationObj
  } catch (error) {
    console.log(
      `error creating traversal distance for relationship ${relationship.id} : ${error.message}`
    )
  }
}

export const prepareRouteSteps = (relationships = []) =>
  relationships.reduce((graph, relationship) => {
    switch (relationship.properties.category) {
      case "elevator":
        return _.merge(graph, createElevatorDistance(relationship))
      case "traversal":
        return _.merge(graph, createTraversalDistance(relationship))
      case "escalator":
        return _.merge(
          graph,
          createVerticalStepDistance(relationship, ESCALATOR_DISTANCE)
        )
      case "stairs":
        return _.merge(
          graph,
          createVerticalStepDistance(relationship, STAIR_DISTANCE)
        )
      default:
        return graph
    }
  }, {})

export const findLocatedUnit = (feature, units) => {
  const { feature_type, properties } = feature
  switch (feature_type) {
    case "kiosk": {
      return properties.anchor.properties.unit
    }
    case "amenity": {
      return properties.units?.[0]
    }
    case "occupant": {
      const { anchor } = properties
      return anchor.properties.unit
    }
    case "geolocation": {
      // Find Units where point is located in
      return units.find((unit) => {
        const polygonal =
          unit.geometry.type === "Polygon"
            ? polygon(unit.geometry.coordinates)
            : multiPolygon(unit.geometry.coordinates)
        return (
          unit.properties.ordinal === properties.ordinal &&
          pointInPolygon(point(feature.geometry.coordinates), polygonal)
        )
      })
    }
    case "unit": {
      return feature
    }
    default: {
      return null
    }
  }
}

export const calculateDisplacement = (
  originGeometry,
  destinationGeometry,
  options = {
    units: "kilometers",
  }
) => {
  const originationPoint = getCenterFromGeometry(originGeometry)
  const destinationPoint = getCenterFromGeometry(destinationGeometry)
  return findDistance(originationPoint, destinationPoint, options)
}

export const createDescription = (
  comingFrom,
  goingTo,
  intermediary,
  nearestLandmark,
  isLastStep
) => {
  if (isLastStep) {
    return {
      text: "To destination",
    }
  }
  if (
    intermediary.every((unit) => unit.properties.category === "escalator") ||
    intermediary.every((unit) => unit.properties.category === "stairs") ||
    intermediary.every((unit) => unit.properties.category === "elevator")
  ) {
    const [intermediate] = intermediary

    return {
      text:
        comingFrom.properties.ordinal < goingTo.properties.ordinal
          ? "To upper level"
          : "To lower level",
      options: {
        category: `${intermediate.feature_type}.${intermediate.properties.category}`,
        destination:
          goingTo.properties?.level?.properties.name ||
          goingTo.properties?.level_name,
      },
    }
  }
  if (goingTo.feature_type === "occupant") {
    return {
      text: "To traverse",
      options: {
        destination: _.get(getFeatureDetail(goingTo), "name"),
      },
    }
  }
  if (
    goingTo.feature_type === "unit" &&
    VERTICAL_TRAVERSAL_CATEGORY.includes(goingTo.properties.category)
  ) {
    return {
      text: "To traverse with landmark",
      options: {
        category: `${goingTo.feature_type}.${goingTo.properties.category}`,
        landmark: _.get(getFeatureDetail(nearestLandmark), "name"),
      },
    }
  }
  return {
    text: "To traverse",
    options: {
      destination: _.get(getFeatureDetail(goingTo), "name"),
    },
  }
}

export const findNearestFeature = (features = [], originateFeature) => {
  return _.minBy(features, (landmark) => {
    const {
      feature_type,
      properties: { anchor },
    } = landmark

    let geometry

    switch (feature_type) {
      case "occupant":
        geometry = anchor.geometry
        break
      default:
        geometry = landmark?.geometry
        break
    }

    if (geometry) {
      const distance = calculateDisplacement(
        geometry,
        originateFeature.geometry
      )
      return distance
    }
  })
}
const getFeatureLocatedGeometry = (feature) => {
  if (!feature) return null
  const {
    feature_type,
    properties: { anchor },
  } = feature
  switch (feature_type) {
    case "occupant":
      return anchor.geometry

    default:
      return feature.geometry
  }
}

export const calculateDistanceBetweenFeatures = (first, second) => {
  const firstGeometry = getFeatureLocatedGeometry(first)
  const secondGeometry = getFeatureLocatedGeometry(second)
  if (firstGeometry && secondGeometry) {
    const distance = calculateDisplacement(firstGeometry, secondGeometry)
    return distance
  }
  return null
}

export const findNearbyPointOfInterest = (
  primary,
  secondary,
  originateFeature
) => {
  if (!primary) return secondary
  const primaryDistance = calculateDistanceBetweenFeatures(
    primary,
    originateFeature
  )
  const secondaryDistance = calculateDistanceBetweenFeatures(
    secondary,
    originateFeature
  )
  // If none of them consider nearby use primary feature
  return primaryDistance < NEARBY_DISTANCE
    ? primary
    : secondaryDistance < NEARBY_DISTANCE
    ? secondary
    : primary
}

export const createArrivalStep = (steps) => {
  const lastStep = _.last(steps)
  const path = point(_.last(lastStep.path.geometry.coordinates))

  return {
    destination: lastStep.destination,
    origin: lastStep.destination,
    intermediary: [],
    path,
    description: { text: "Arrived at destination" },
  }
}

export const groupFeatureByOrdinal = (features) => {
  return _.groupBy(features, (feature) => {
    switch (feature.feature_type) {
      case "occupant": {
        return feature.properties.anchor.properties.unit.properties.ordinal
      }
      default: {
        return feature.properties.ordinal
      }
    }
  })
}
